'use client';
import { useState, useEffect } from 'react';
import { useParams } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { AccountActStatus } from '@nf/utils-common/constants';
import { PopUpFeatureId } from '@nf/types/account';
import { isOddsPageLoadingAtom } from '@/atoms/odds-page';
import { useViewMode } from '@/store/appearance';
import { accountService } from '@/services/account.service';
import { is1x2CustomizationSettingSport } from '@/services/settings.service';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useAccountInfo } from '@/hooks/query/account';
import { useBreakpoint } from '@/features/breakpoint/contexts';
import { Breakpoint } from '@/features/breakpoint';

import { appbarStyles } from '../../appbar.style';

export const HighlightEffect = () => {
	const params = useParams();
	const queryClient = useQueryClient();

	const sportId = params.sport as string | undefined;

	const { breakpoint } = useBreakpoint();
	const isMobile = breakpoint <= Breakpoint.sm;

	const isLoading = useAtomValue(isOddsPageLoadingAtom);

	const { SiteConfigsData } = useGetSiteConfigs();
	const { user } = useAccountInfo();
	const userSetting = user?.UserSetting;
	const { view, isMars } = useViewMode();

	const hasShownHighlight = accountService.getHasShownFeatureId(
		userSetting,
		PopUpFeatureId.BettypeModeNotice
	);

	const shouldShowHighlight = user?.ActStatus === AccountActStatus.Deposited && 
			isMars && 
			SiteConfigsData?.Enable1x2Customization && 
			SiteConfigsData?.Enable1x2CustomizationHint && 
			is1x2CustomizationSettingSport(Number(sportId)) && 
			isMobile && 
			!hasShownHighlight && 
			!isLoading;

	const [showHighlight, setShowHighlight] = useState(false);

	useEffect(() => {
		if (shouldShowHighlight) {
			const showTimer = setTimeout(() => {
				setShowHighlight(true);
			}, 500);
	
			const hideTimer = setTimeout(() => {
				setShowHighlight(false);
				accountService.setHasShownFeatureId(queryClient, userSetting, PopUpFeatureId.BettypeModeNotice);
			}, 3500);
	
			return () => {
				clearTimeout(showTimer);
				clearTimeout(hideTimer);
			};
		}
	}, [isMobile, view, isLoading, userSetting, shouldShowHighlight, queryClient]);

	if (!shouldShowHighlight || !showHighlight) {
		return null;
	}

	return (
		<div className={appbarStyles.highlight}>
			<div className={appbarStyles.circle}>
				<div className={appbarStyles.innerCircle}>
					<div className={appbarStyles.coreCircle} />
				</div>
			</div>
		</div>
	);
};